import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { useLanguage } from "contexts/language.context";
import { useEffect, useMemo, useState } from "react";
import DropdownSelect from "../DropdownSelect";
import { usePermissionMonitor } from "../PermissionMonitor";
import levelsMap from "./levelsMap";

export default function SelectionGrid({ level = ["account"], xs, ...props }) {
  const { label } = useLanguage();

  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    async function initFetch() {
      await Promise.all(
        level.map(async (lvl) => {
          const config = levelsMap[lvl];
          if (config && config.fetch) {
            setLoading((prev) => ({ ...prev, [lvl]: true }));
            const [ok, data] = await config.fetch();
            if (ok) {
              setOptions((prev) => ({ ...prev, [lvl]: data }));
            }
            setLoading((prev) => ({ ...prev, [lvl]: false }));
          }
        }),
      );
    }
    initFetch();
  }, []);

  const dropdownOptions = useMemo(() => {
    const result = {};

    level.forEach((lvl, index) => {
      const rawOptions = options[lvl] || [];

      const filteredOptions = rawOptions.filter((item) => {
        let currentItem = item;

        // Crawl up the hierarchy to validate against selected parents
        for (let i = index; i > 0; i--) {
          const currentConfig = levelsMap[level[i]];
          const parentLevel = level[i - 1];
          const parentConfig = levelsMap[parentLevel];

          const parentId = currentItem[currentConfig.parentKey];
          const selectedParentId = props[parentConfig.valueProp];

          // If a parent is actively selected in state, the item's lineage MUST match it
          if (selectedParentId && parentId !== selectedParentId) {
            return false;
          }

          // Move item reference up the tree for the next loop iteration
          if (i > 1) {
            const parentRawOptions = options[parentLevel] || [];
            currentItem = parentRawOptions.find((p) => p.id === parentId);
            if (!currentItem && selectedParentId) return false;
          }
        }
        return true;
      });

      result[lvl] = filteredOptions.map((item) => ({
        id: item.id,
        label: item.name || item.label,
        ...item,
      }));
    });

    return result;
  }, [
    options,
    level,
    levelsMap,
    ...level.map((lvl) => props[levelsMap[lvl]?.valueProp]),
  ]);

  const handleSelectionChange = (lvl, newValueId) => {
    const config = levelsMap[lvl];

    // 1. Update the specifically targeted level
    if (props[config.setterProp]) {
      props[config.setterProp](newValueId);
    }

    if (newValueId) {
      // --- UPWARD TRAVERSAL: Auto-select missing parents ---
      let currentEntity = lvl;
      let currentItem = (options[currentEntity] || []).find(
        (o) => o.id === newValueId,
      );

      while (currentEntity && currentItem) {
        const currConfig = levelsMap[currentEntity];
        const parentEntity = currConfig.parentLevel;
        const parentKey = currConfig.parentKey;

        if (parentEntity && parentKey) {
          const parentId = currentItem[parentKey];
          const pConfig = levelsMap[parentEntity];

          if (parentId) {
            const currentParentValue = props[pConfig.valueProp];

            // Auto-update if parent doesn't match AND parent is present in the current level array
            if (
              currentParentValue !== parentId &&
              props[pConfig.setterProp] &&
              level.includes(parentEntity)
            ) {
              props[pConfig.setterProp](parentId);
            }

            // Move pointer up the tree for the next loop
            currentEntity = parentEntity;
            currentItem = (options[parentEntity] || []).find(
              (o) => o.id === parentId,
            );
          } else {
            break;
          }
        } else {
          break;
        }
      }

      // --- DOWNWARD TRAVERSAL: Clear children that no longer match the new lineage ---
      level.forEach((childLevel) => {
        // Skip root levels or the level we just changed
        if (childLevel === lvl || !levelsMap[childLevel].parentLevel) return;

        const childConfig = levelsMap[childLevel];
        const selectedChildId = props[childConfig.valueProp];

        if (selectedChildId) {
          let traceEntity = childLevel;
          let traceItem = (options[traceEntity] || []).find(
            (o) => o.id === selectedChildId,
          );
          let isValid = true;

          // Trace this child's lineage upwards to see if it connects properly to `lvl`
          while (traceEntity && traceItem) {
            const tConfig = levelsMap[traceEntity];
            const pEntity = tConfig.parentLevel;

            if (pEntity === lvl) {
              // We hit the level that just changed. Validate the ID!
              if (traceItem[tConfig.parentKey] !== newValueId) {
                isValid = false;
              }
              break; // Stop tracing up
            } else if (pEntity) {
              // Not there yet, move up one step
              const pId = traceItem[tConfig.parentKey];
              traceEntity = pEntity;
              traceItem = (options[pEntity] || []).find((o) => o.id === pId);
            } else {
              break;
            }
          }

          if (!isValid && props[childConfig.setterProp]) {
            props[childConfig.setterProp](null);
          }
        }
      });
    } else {
      // --- CASCADING CLEAR: If a parent is cleared, wipe all hierarchical descendants ---
      level.forEach((childLevel) => {
        let traceEntity = childLevel;
        let isDescendant = false;

        // Trace up to see if the cleared `lvl` is an ancestor of this child
        while (traceEntity) {
          const pEntity = levelsMap[traceEntity]?.parentLevel;
          if (pEntity === lvl) {
            isDescendant = true;
            break;
          }
          traceEntity = pEntity;
        }

        if (isDescendant) {
          const childConfig = levelsMap[childLevel];
          if (props[childConfig.valueProp] && props[childConfig.setterProp]) {
            props[childConfig.setterProp](null);
          }
        }
      });
    }
  };

  return (
    <>
      {level.map((lvl) => {
        const config = levelsMap[lvl];
        if (!config) return null;

        return (
          <Selector
            requiresAdminForAll={lvl == "account" ? true : false}
            key={lvl}
            value={props[config.valueProp]}
            onChange={(newId) => handleSelectionChange(lvl, newId)}
            items={dropdownOptions[lvl] || []}
            loading={loading[lvl] || false}
            label={label(config.labelKey)}
            groupBy={config.groupBy}
            xs={xs}
          />
        );
      })}
    </>
  );
}

export function Selector({
  requiresAdminForAll,
  label,
  value,
  onChange,
  items,
  loading,
  groupBy,
  xs = 12,
}) {
  const can = usePermissionMonitor();
  const { label: languageLabel } = useLanguage();

  const allowed = can("isAdmin") || !requiresAdminForAll;

  return (
    <>
      <Grid item xs={allowed ? xs * (2 / 3) : xs}>
        <DropdownSelect
          label={label}
          items={items}
          initialValue={value || ""}
          disabled={allowed ? !value : false}
          required
          onChange={(newValue) => {
            onChange(newValue?.id || null);
          }}
          disableClearable={true}
          loading={loading}
          groupBy={groupBy}
        />
      </Grid>
      {allowed ? (
        <Grid item xs={xs * (1 / 3)}>
          <FormControlLabel
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
            label={
              <Typography
                sx={(theme) => ({
                  fontSize: "14px",
                  fontWeight: "450",
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                  color: theme.palette.text.primary,
                })}
              >
                {languageLabel("available-for-everyone")
                  .trim()
                  .split(" ")
                  .map((v, i) =>
                    i == 0 ? (
                      <>
                        {v}
                        <br />
                      </>
                    ) : (
                      v + " "
                    ),
                  )}
              </Typography>
            }
            control={
              <Checkbox
                checked={!value}
                onChange={(e) => {
                  if (!e.target.checked) {
                    onChange(items?.[0]?.id || -1);
                  } else {
                    onChange(null);
                  }
                }}
              />
            }
          />
        </Grid>
      ) : null}
    </>
  );
}
